import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, ArrowUpRight, Users, CreditCard, MailCheck, MessageSquare, Archive, UserCheck, ListFilter, ArrowDownUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { JET_PROGRAMS, STATUS, STATUS_GROUPS, canViewAll, fmtDate, isStaff, money } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { PageHero } from "@/components/PageHero";
import { ClientTypeBadge } from "@/components/ClientTypeBadge";
import { AsapBadge } from "@/components/AsapBadge";
import AcknowledgeDialog from "@/components/AcknowledgeDialog";
import TrashBin from "@/components/TrashBin";
import { saveTripNav } from "@/components/TripSwipeNav";
import QuoteFromEmail from "@/components/QuoteFromEmail";
import OptionDialog from "@/components/OptionDialog";

const QUOTING = ["new", "options_sent", "booking_requested"];

const QuickAddButton = ({ t, onAdd, className = "" }) => (
  <button type="button" className={`frame-btn !py-2 !px-3 !text-[0.6rem] ${className}`}
    onClick={(e) => { e.stopPropagation(); onAdd(t); }} onKeyDown={(e) => e.stopPropagation()} data-testid={`trip-row-${t.ref}-add-option-btn`}>
    <Plus size={12} /> Add option
  </button>
);

const AckButton = ({ t, onAck, className = "" }) => (
  <button type="button" className={`frame-btn gold ack-pulse !py-2 !px-3 !text-[0.6rem] ${className}`}
    onClick={(e) => { e.stopPropagation(); onAck(t); }} onKeyDown={(e) => e.stopPropagation()} data-testid={`trip-row-${t.ref}-ack-btn`}>
    <MailCheck size={12} /> Acknowledge
  </button>
);

function ClientFilter({ value, onChange }) {
  return (
    <div className="relative w-full sm:w-64">
      <CreditCard size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10 pointer-events-none" />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="pl-9 bg-[#111319] border-white/10 h-11 max-lg:rounded-xl" data-testid="client-filter-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all" data-testid="client-filter-all">All client types</SelectItem>
          <SelectItem value="on_demand" data-testid="client-filter-on_demand">On Demand Charter</SelectItem>
          <SelectItem value="jet_card" data-testid="client-filter-jet_card">Jet Card · all programs</SelectItem>
          {Object.entries(JET_PROGRAMS).map(([k, p]) => (
            <SelectItem key={k} value={`jet_card:${k}`} data-testid={`client-filter-jet_card-${k}`}>Jet Card · {p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

const SORTS = [["newest", "Newest request first"], ["soonest", "Soonest trip date first"], ["latest", "Latest trip date first"]];

function SortSelect({ value, onChange }) {
  return (
    <div className="relative w-full md:w-64 shrink-0">
      <ArrowDownUp size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10 pointer-events-none" />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="pl-9 bg-[#111319] border-white/10 h-11 max-lg:rounded-xl" data-testid="trip-sort-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {SORTS.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`trip-sort-option-${k}`}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatTile({ k, label, count, active, onClick, alert = false }) {
  return (
    <button onClick={onClick} data-testid={`status-filter-${k}`}
      className={`lux-card m-card hoverable text-left p-4 sm:p-5 ${active ? "!border-[#5B7FD1]/60 bg-[#14161c]" : alert ? "!border-[#1E3A8A]/60" : ""}`}>
      <span className={`flex items-center gap-1.5 text-[0.6rem] tracking-[0.24em] uppercase ${alert ? "text-[#1E3A8A] font-semibold" : "text-zinc-500"}`}>
        {k === "awaiting" && <MessageSquare size={11} />}{k === "assigned" && <UserCheck size={11} />}{label}
      </span>
      <span className="block font-serif-display text-3xl sm:text-4xl text-white mt-2" data-testid={`status-filter-${k}-count`}>{count}</span>
    </button>
  );
}


function StatusFilter({ value, onChange, items }) {
  return (
    <div className="relative w-full md:w-64 shrink-0">
      <ListFilter size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10 pointer-events-none" />
      <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
        <SelectTrigger className="pl-9 bg-[#111319] border-white/10 h-11 max-lg:rounded-xl" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {items.map((it) => (
            <SelectItem key={it.k} value={it.k} data-testid={`status-filter-option-${it.k}`}>{it.label} ({it.count})</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function TripRow({ t, staff, viewAll, i, onAck, onAdd }) {
  const navigate = useNavigate();
  const first = t.legs?.[0];
  const needsAck = staff && !t.acknowledged_at && t.status === "new";
  const canAdd = staff && !!t.acknowledged_at && QUOTING.includes(t.status);
  const open = () => navigate(`/trips/${t.id}`);
  return (
    <div role="link" tabIndex={0} onClick={open} onKeyDown={(e) => { if (e.key === "Enter") open(); }} data-testid={`trip-row-${t.ref}`}
      style={{ animationDelay: `${Math.min(i, 10) * 40}ms` }}
      className={`rise lux-card m-card hoverable group cursor-pointer w-full text-left grid grid-cols-2 md:grid-cols-[2fr_1fr_0.5fr_1fr_0.9fr_230px] gap-x-4 gap-y-2 items-center px-5 py-4 ${needsAck ? "needs-ack" : ""}`}>
      <span className="col-span-2 md:col-span-1 min-w-0 flex flex-col items-start gap-1.5">
        <span className="flex w-full items-start justify-between gap-3">
          <span className="font-mono-data text-white text-sm sm:text-[15px] break-words" data-testid={`trip-row-title-${t.id}`}>{t.ref}</span>
          <span className="md:hidden shrink-0"><StatusBadge status={t.status} /></span>
        </span>
        <span className="flex flex-wrap gap-1.5">
          {needsAck && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.6rem] tracking-[0.18em] uppercase border border-amber-400 bg-amber-400 text-black font-semibold" data-testid={`trip-row-${t.ref}-needs-ack`}>
              <span className="h-1.5 w-1.5 rounded-full bg-black animate-pulse" /> Needs acknowledgment
            </span>
          )}
          {staff && t.awaiting_reply && (
            <span className="on-navy inline-flex items-center gap-1.5 px-2.5 py-1 text-[0.6rem] tracking-[0.18em] uppercase bg-[#1E3A8A] text-white font-semibold" data-testid={`trip-row-${t.ref}-question-waiting`}>
              <MessageSquare size={11} /> Question waiting
            </span>
          )}
          <AsapBadge trip={t} testId={`trip-row-${t.ref}-asap`} />
          <ClientTypeBadge trip={t} testId={`trip-row-${t.ref}-client-badge`} />
        </span>
      </span>
      <span className="text-zinc-400 text-xs">{fmtDate(first?.date)}{t.legs.length > 1 ? ` · ${t.legs.length} legs` : ""}</span>
      <span className="text-zinc-400 text-xs">{Math.max(...t.legs.map((l) => l.pax))} pax</span>
      <span className="text-zinc-400 text-xs truncate">{viewAll ? t.broker_name : t.client_reference || "—"}{viewAll && t.assigned_to_name && <span className="block text-[11px] text-[#5B7FD1] truncate" data-testid={`trip-row-${t.ref}-assignee`}>Assigned: {t.assigned_to_name}</span>}</span>
      <span className="text-xs">
        <span className="text-white">{t.option_count} opt.</span>
        {t.lowest_price != null && <span className="text-zinc-500"> · from {money(t.lowest_price)}</span>}
      </span>
      <span className="hidden md:flex flex-col items-end gap-2">
        <span className="flex items-center gap-3">
          <StatusBadge status={t.status} />
          <ArrowUpRight size={15} className="text-zinc-600 group-hover:text-[#5B7FD1] transition-colors" />
        </span>
        {needsAck && <AckButton t={t} onAck={onAck} className="mr-7" />}
        {canAdd && <QuickAddButton t={t} onAdd={onAdd} className="mr-7" />}
      </span>
      {needsAck && <span className="md:hidden col-span-2"><AckButton t={t} onAck={onAck} className="w-full" /></span>}
      {canAdd && <span className="md:hidden col-span-2"><QuickAddButton t={t} onAdd={onAdd} className="w-full" /></span>}
    </div>
  );
}

export default function Dashboard() {
  const api = useApi();
  const { me } = useMe();
  const staff = isStaff(me);
  const viewAll = canViewAll(me);
  const exec = me.role === "executive";
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [brokerId, setBrokerId] = useState("all");
  const [client, setClient] = useState("all");
  const [ackTrip, setAckTrip] = useState(null);
  const [addTrip, setAddTrip] = useState(null);
  const [archived, setArchived] = useState(false);
  const [sort, setSort] = useState("newest");
  const mine = status === "assigned";
  const [brokers, setBrokers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (viewAll) api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => {});
  }, [api, viewAll]);

  const load = useCallback(async () => {
    try {
      const [clientType, program] = client === "all" ? [] : client.split(":");
      const res = await api.get("/trips", { params: { status: status && !mine ? status : undefined, assigned: mine ? "me" : undefined, q: q || undefined, broker_id: brokerId !== "all" ? brokerId : undefined, client_type: clientType, program, archived: archived || undefined, sort: sort !== "newest" ? sort : undefined } });
      setData(res.data);
      saveTripNav(res.data.trips, "trips");
      setError(null);
    } catch (e) {
      setError(errMsg(e));
    }
  }, [api, status, mine, q, brokerId, client, archived, sort]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const total = data ? Object.values(data.counts).reduce((a, b) => a + b, 0) : 0;
  const statusItems = [
    { k: "all", label: "All statuses", count: total },
    ...(viewAll ? [{ k: "awaiting", label: "Questions waiting", count: data?.awaiting_count ?? 0 }] : []),
    ...(me.role === "flight_department" ? [{ k: "assigned", label: "Assigned to me", count: data?.assigned_count ?? 0 }] : []),
    ...Object.entries(STATUS).map(([k, v]) => ({ k, label: v.label, count: data?.counts[k] ?? 0 })),
  ];
  return (
    <div data-testid="dashboard-page">
      <PageHero testId="dashboard-hero">
        <div className="flex flex-col sm:flex-row sm:items-end gap-6 justify-between">
          <div className="rise">
            <p className="eyebrow mb-3">{staff ? "Flight Department Desk" : exec ? `Executive · ${me.name}` : `Broker · ${me.name}`}</p>
            <h1 className="font-serif-display text-[2rem] sm:text-4xl lg:text-5xl text-white leading-[1.12] lg:leading-tight max-lg:uppercase">
              {viewAll ? "Trip Requests" : "Your Trips"}
            </h1>
            <p className="text-zinc-400 text-sm mt-4 max-w-md">
              {staff ? "Every broker's requests and certified charter options, ranked by price." : exec ? "Every broker's trips, view only. Trips you request yourself are yours to manage." : "Options from our certified charter operators, ranked lowest price first."}
            </p>
          </div>
          <div className="flex flex-wrap gap-2 self-start sm:self-auto">
            {staff && <QuoteFromEmail onAdded={load} />}
            <Link to="/trips/new" className="frame-btn gold" data-testid="dashboard-new-request-btn">
              <Plus size={14} /> {staff ? "New Request" : "Request a Quote"}
            </Link>
          </div>
        </div>
      </PageHero>

      <div className="m-overlap hidden md:grid grid-cols-5 gap-3 mb-8" data-testid="dashboard-stat-tiles">
        <StatTile k="all" label="All" count={total} active={!status} onClick={() => setStatus("")} />
        {viewAll && (
          <StatTile k="awaiting" label="Questions waiting" count={data?.awaiting_count ?? 0} active={status === "awaiting"}
            alert={(data?.awaiting_count ?? 0) > 0} onClick={() => setStatus("awaiting")} />
        )}
        {me.role === "flight_department" && (
          <StatTile k="assigned" label="Assigned to me" count={data?.assigned_count ?? 0} active={mine} onClick={() => setStatus("assigned")} />
        )}
        {Object.entries(STATUS).map(([k, v]) => (
          <StatTile key={k} k={k} label={v.label} count={data?.counts[k] ?? 0} active={status === k} onClick={() => setStatus(k)} />
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="dashboard-group-filter">
        <span className="text-[0.6rem] tracking-[0.22em] uppercase text-zinc-500 mr-1">Stage</span>
        {STATUS_GROUPS.map((g) => {
          const n = Object.entries(STATUS).filter(([, v]) => v.group === g.key).reduce((a, [k]) => a + (data?.counts[k] ?? 0), 0);
          const on = status === g.key;
          return (
            <button key={g.key} type="button" onClick={() => setStatus(on ? "" : g.key)} data-testid={`group-filter-${g.key}`}
              className={`inline-flex items-center gap-2 px-3 py-1.5 border text-xs transition-colors ${on ? "text-white" : "text-zinc-300 border-white/10 hover:border-white/30"}`}
              style={on ? { background: g.color, borderColor: g.color } : undefined}>
              <span className="h-2 w-2 rounded-full" style={{ background: on ? "#fff" : g.color }} /> {g.label} <span className="opacity-70">{n}</span>
            </button>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row md:flex-wrap gap-3 mb-5 max-md:mt-6">
      {viewAll ? (
        <div className="relative w-full md:w-72 md:flex-none">
          <Users size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 z-10 pointer-events-none" />
          <Select value={brokerId} onValueChange={setBrokerId}>
            <SelectTrigger className="pl-9 bg-[#111319] border-white/10 h-11 max-lg:rounded-xl" data-testid="broker-filter-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="broker-filter-all">All brokers</SelectItem>
              {brokers.map((b) => <SelectItem key={b.id} value={b.id} data-testid={`broker-filter-${b.id}`}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ) : (
        <div className="relative w-full max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search ref, airport, client…"
            className="pl-9 bg-[#111319] border-white/10 h-11 max-lg:rounded-xl" data-testid="trip-search-input" />
        </div>
      )}
        <ClientFilter value={client} onChange={setClient} />
        <StatusFilter value={status} onChange={setStatus} items={statusItems} />
        <SortSelect value={sort} onChange={setSort} />
        {me.role === "admin" && (
          <button className={`frame-btn !py-2.5 shrink-0 ${archived ? "gold" : ""}`} onClick={() => setArchived((a) => !a)} data-testid="archive-toggle-btn">
            <Archive size={13} /> {archived ? "Back to active trips" : `Archive (${data?.archived_count ?? 0})`}
          </button>
        )}
        <TrashBin count={data?.trash_count ?? 0} onChanged={load} />
      </div>
      {archived && <p className="text-xs text-zinc-500 mb-4" data-testid="archive-mode-note">Showing archived trips. Only admins can see these.</p>}

      <div className="hidden md:grid grid-cols-[2fr_1fr_0.5fr_1fr_0.9fr_230px] gap-x-4 px-5 pb-3 text-[0.6rem] tracking-[0.22em] uppercase text-zinc-600">
        <span>Request</span><span>Departure</span><span>Pax</span><span>{viewAll ? "Broker" : "Client ref"}</span><span>Options</span><span className="text-right">Status</span>
      </div>
      <div className="space-y-2" data-testid="trip-list">
        {error && <div className="lux-card px-6 py-6 text-rose-300 text-sm" data-testid="trip-list-error">{error}</div>}
        {!data && !error && <div className="py-16 grid place-items-center"><div className="jet-loader" /></div>}
        {data && data.trips.length === 0 && (
          <div className="lux-card m-card px-6 py-16 text-center" data-testid="trip-list-empty">
            <p className="font-serif-display text-3xl text-white">{archived ? "The archive is empty" : mine ? "Nothing assigned to you" : status === "awaiting" ? "No questions waiting" : "No trips yet"}</p>
            <p className="text-zinc-500 text-sm mt-2">{status === "awaiting" ? "Every broker question has a reply." : staff ? "New broker requests will appear here." : "Submit your first quote request to get started."}</p>
          </div>
        )}
        {data?.trips.map((t, i) => <TripRow key={t.id} t={t} staff={staff} viewAll={viewAll} i={i} onAck={setAckTrip} onAdd={setAddTrip} />)}
      </div>
      {staff && addTrip && (
        <OptionDialog open={!!addTrip} tripId={addTrip.id} trip={addTrip} option={null} onOpenChange={(o) => { if (!o) setAddTrip(null); }} onSaved={load} />
      )}
      {staff && ackTrip && (
        <AcknowledgeDialog trip={ackTrip} open={!!ackTrip} onOpenChange={(o) => { if (!o) setAckTrip(null); }} onSaved={load} />
      )}
    </div>
  );
}
